// Packages
import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";

// Types
import { TThemeColors } from "src/interface";

// Utils
import { buildColorClasses } from "@utils/theme";
import { computeDropdownPlacement } from "@utils/placement";
import { defaultListSearch, FxListMatch, FxListSearchResult } from "@utils/list";

/**
 * A plain-data description of an option, passed to a `customSearch` callback so
 * consumers can filter without touching the DOM.
 */
export interface FxSelectOption {
  value: string;
  label: string;
  disabled: boolean;
  selected: boolean;
}

/**
 * Custom search callback. Receives the current query and the full option list
 * and returns the options that should stay visible. May be async.
 */
export type FxSelectSearch = (
  query: string,
  options: FxSelectOption[]
) => FxSelectOption[] | Promise<FxSelectOption[]>;

/** Monotonic counter used to build unique option ids per select instance. */
let selectInstanceCounter = 0;

/**
 * Structural type for the `fx-select-option` elements managed by the select.
 */
interface FxSelectOptionEl extends HTMLElement {
  value: string;
  id: string;
  label?: string;
  disabled: boolean;
  selected: boolean;
  filtered: boolean;
  active: boolean;
  highlightQuery?: string;
}

/**
 * `fx-select` is a headless, accessible select component. It renders a trigger
 * showing the selected label(s) and a dropdown list populated by slotted
 * `fx-select-option` children. It supports single/multiple selection, search
 * (with an optional custom matcher), a loading state, keyboard navigation and
 * native form association. Every styleable surface is exposed via `part`
 * attributes and its state via `data-*`/`aria-*` attributes.
 *
 * @slot - The `fx-select-option` elements (and any other slotted content).
 *
 * @part trigger - The button that opens/closes the list and shows the value.
 * @part value - The element that displays the selected label(s).
 * @part placeholder - Shown inside the trigger when nothing is selected.
 * @part indicator - The open/close indicator inside the trigger.
 * @part popover - The floating dropdown container (search, status, list, states).
 * @part list - The `role="listbox"` element that holds the options.
 * @part status - Visually hidden live region announcing the active option.
 * @part search-input - The search input rendered when `search` is enabled.
 * @part loading - The loading indicator rendered when `loading` is enabled.
 * @part empty - Shown when the filtered option list is empty.
 */
// `styleUrl` is intentionally omitted (no internal styles per AGENTS.md).
@Component({
  tag: "fx-select",
  shadow: true,
  formAssociated: true,
})
export class FxSelect {
  // --------------------------------------------
  // Element / internal references
  // --------------------------------------------

  @Element() el!: HTMLElement;

  @AttachInternals() internals!: ElementInternals;

  private searchInputRef?: HTMLInputElement;

  private triggerRef?: HTMLButtonElement;

  private listRef?: HTMLDivElement;

  /** Registered `fx-select-option` elements, kept in DOM order. */
  private options: FxSelectOptionEl[] = [];

  /** Token used to ignore stale async `customSearch` results. */
  private searchToken = 0;

  /** Handle for the placement/focus frame scheduled on open, cancelled on disconnect. */
  private openFrame?: number;

  /** Handle for the throttled reposition frame, cancelled on close/disconnect. */
  private repositionFrame?: number;

  /** Unique id fragment for this instance, used to build option ids. */
  private readonly instanceId = ++selectInstanceCounter;

  /** Stable id of the listbox, referenced by the combobox via aria-controls. */
  private readonly listId = `fx-select-${this.instanceId}-listbox`;

  /** Incrementing counter used to give registered options stable ids. */
  private optionIdCounter = 0;

  /** Guards against re-entrant `value` watcher runs when parsing attributes. */
  private parsingValue = false;

  /** Guards the `value` watcher while the component commits an internal change. */
  private committingValue = false;

  /** Closes the list when a pointer press happens outside the component. */
  private onDocumentPointerDown = (ev: Event) => {
    const path = ev.composedPath();
    if (!path.includes(this.el)) {
      this.close();
    }
  };

  /**
   * Recomputes the dropdown placement while the list is open, throttled to one
   * layout read per animation frame to avoid thrashing during scroll/resize.
   */
  private onReposition = () => {
    if (this.repositionFrame !== undefined) {
      return;
    }
    this.repositionFrame = requestAnimationFrame(() => {
      this.repositionFrame = undefined;
      this.updatePlacement();
    });
  };

  // --------------------------------------------
  // State
  // --------------------------------------------

  /** Whether the dropdown list is open. */
  @State() private open = false;

  /** The current search query. */
  @State() private query = "";

  /** The value of the active (keyboard-highlighted) option. */
  @State() private activeValue?: string;

  /** Number of options currently visible after filtering. */
  @State() private visibleCount = 0;

  /**
   * Where the dropdown list is rendered relative to the trigger. Flips to
   * `"top"` when there is not enough room below to avoid clipping.
   */
  @State() private placement: "bottom" | "top" = "bottom";

  /** Bumped to force a re-render when the registered option set changes. */
  @State() private optionsVersion = 0;

  /** Whether the initial option set has been registered after first render. */
  @State() private optionsReady = false;

  // --------------------------------------------
  // Props
  // --------------------------------------------

  /**
   * @prop - If `true`, the value cannot be changed but the list can still be
   * opened to view the options.
   */
  @Prop() readonly: boolean = false;

  /**
   * @prop - If `true`, the select is disabled and cannot be interacted with.
   */
  @Prop() disabled: boolean = false;

  /**
   * @prop - The selected value. A single string for single selection, or an
   * array (or JSON array string) when `multiple` is enabled. `mutable` so the
   * component can update it as the user selects options.
   */
  @Prop({ mutable: true }) value?: string | string[];

  /**
   * @prop - The theme color applied to the trigger/list. Applies
   * `fx-color-{color}` and `fx-color-theme` classes and reflects to
   * `data-color`.
   */
  @Prop() color?: TThemeColors | string = undefined;

  /**
   * @prop - The size of the select. Reflects to `data-size` on the host.
   */
  @Prop() size: "xs" | "sm" | "md" | "lg" | "xl" = "md";

  /**
   * @prop - The visual variant of the select. Reflects to `data-variant`.
   */
  @Prop() variant: "outline" | "solid" | "soft" = "outline";

  /**
   * @prop - If `true`, multiple options can be selected and `value` is an array.
   */
  @Prop() multiple: boolean = false;

  /**
   * @prop - The control name, submitted with the enclosing form.
   */
  @Prop() name?: string;

  /**
   * @prop - If `true`, a value must be selected before form submission.
   */
  @Prop() required: boolean = false;

  /**
   * @prop - If `true`, a loading indicator is shown in the list.
   */
  @Prop() loading: boolean = false;

  /**
   * @prop - If `true`, a search input is shown that filters the options.
   */
  @Prop() search: boolean = false;

  /**
   * @prop - Optional custom search function. When provided it is used instead
   * of the default case-insensitive substring matching. May return a promise.
   */
  @Prop() customSearch?: FxSelectSearch;

  // --------------------------------------------
  // Events
  // --------------------------------------------

  /**
   * @event - Emitted when the selection changes, with `{ value }`.
   */
  @Event() fxChange!: EventEmitter<{ value: string | string[] }>;

  /**
   * @event - Emitted when the trigger receives focus.
   */
  @Event() fxFocus!: EventEmitter<void>;

  /**
   * @event - Emitted when the trigger loses focus.
   */
  @Event() fxBlur!: EventEmitter<void>;

  /**
   * @event - Emitted when the search query changes, with `{ query }`.
   */
  @Event() fxInput!: EventEmitter<{ query: string }>;

  // --------------------------------------------
  // Lifecycle
  // --------------------------------------------

  componentWillLoad() {
    this.parseValueAttribute();
    this.syncFormValue();
  }

  componentDidLoad() {
    this.optionsReady = true;
    this.refreshOptions();
  }

  disconnectedCallback() {
    if (this.openFrame !== undefined) {
      cancelAnimationFrame(this.openFrame);
      this.openFrame = undefined;
    }
    if (this.repositionFrame !== undefined) {
      cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = undefined;
    }
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    window.removeEventListener("resize", this.onReposition);
    window.removeEventListener("scroll", this.onReposition, true);
  }

  // --------------------------------------------
  // Watchers
  // --------------------------------------------

  @Watch("value")
  valueChanged() {
    if (this.parsingValue || this.committingValue) {
      return;
    }
    this.parseValueAttribute();
    this.syncFormValue();
    this.syncOptions();
  }

  @Watch("required")
  requiredChanged() {
    this.syncValidity();
  }

  @Watch("multiple")
  multipleChanged() {
    this.parseValueAttribute();
    this.syncFormValue();
    this.syncOptions();
  }

  @Watch("disabled")
  disabledChanged() {
    // A disabled control cannot be dismissed by keyboard (handlers short-circuit)
    // nor by its now-disabled trigger, so close the list proactively.
    if (this.disabled && this.open) {
      this.close();
    }
    // Disabled controls are excluded from form submission and validation.
    this.syncFormValue();
  }

  @Watch("loading")
  @Watch("search")
  optionalsChanged() {
    this.applySearch();
  }

  // --------------------------------------------
  // Public API (called by child fx-select-option elements)
  // --------------------------------------------

  /**
   * @method - Registers (or re-syncs) an option. Called by `fx-select-option`
   * on connect.
   */
  @Method()
  async registerOption(_option: HTMLElement): Promise<void> {
    this.refreshOptions();
  }

  /**
   * @method - Deregisters an option. Called by `fx-select-option` on disconnect.
   */
  @Method()
  async unregisterOption(_option: HTMLElement): Promise<void> {
    this.refreshOptions();
  }

  /**
   * @method - Selects an option in response to a click routed from a child
   * option.
   */
  @Method()
  async handleOptionClick(option: HTMLElement): Promise<void> {
    const value = (option as FxSelectOptionEl).value;
    const disabled = (option as FxSelectOptionEl).disabled;
    if (disabled) {
      return;
    }
    this.selectValue(value);
  }

  // --------------------------------------------
  // Private helpers
  // --------------------------------------------

  private refreshOptions(): void {
    this.options = Array.from(
      this.el.querySelectorAll("fx-select-option")
    ) as FxSelectOptionEl[];
    // Give every option a stable id so the listbox can reference the active
    // one via aria-activedescendant.
    this.options.forEach((option) => {
      if (!option.id) {
        option.id = `fx-select-${this.instanceId}-option-${this.optionIdCounter++}`;
      }
    });
    this.optionsVersion++;
    this.applySearch();
  }

  /**
   * The label announced by the live region while navigating. Slotted options
   * live in the light DOM, so a cross-root `aria-activedescendant` reference
   * from the shadow trigger/search input is not reliably announced; a polite
   * live region guarantees the active option is read out.
   */
  private get activeAnnouncement(): string {
    if (!this.open || this.activeValue === undefined) {
      return "";
    }
    const active = this.options.find(
      (option) => option.value === this.activeValue
    );
    return active ? this.optionLabel(active) : "";
  }

  /** The id of the active (keyboard-highlighted) option, if any. */
  private get activeDescendantId(): string | undefined {
    if (!this.open || this.activeValue === undefined) {
      return undefined;
    }
    const active = this.options.find(
      (option) => option.value === this.activeValue
    );
    return active?.id || undefined;
  }

  /**
   * Whether the given node belongs to this component — either its shadow root
   * (trigger/search input) or its light-DOM subtree (slotted options). Both
   * count as internal so clicking an option does not read as a focus exit.
   */
  private isInternalFocusTarget(node: EventTarget | null): boolean {
    if (!node) {
      return false;
    }
    const target = node as Node;
    return (
      (!!this.el.shadowRoot && this.el.shadowRoot.contains(target)) ||
      this.el.contains(target)
    );
  }

  private getValueArray(): string[] {
    const { value } = this;
    if (Array.isArray(value)) {
      return value;
    }
    return value !== undefined && value !== null && value !== ""
      ? [value]
      : [];
  }

  private parseValueAttribute(): void {
    if (this.multiple) {
      if (typeof this.value === "string") {
        const raw = this.value.trim();
        this.parsingValue = true;
        if (raw.startsWith("[")) {
          try {
            const parsed = JSON.parse(raw);
            this.value = Array.isArray(parsed)
              ? parsed.filter((entry) => typeof entry === "string")
              : [];
          } catch {
            this.value = raw === "" ? [] : [raw];
          }
        } else {
          this.value = raw === "" ? [] : [raw];
        }
        this.parsingValue = false;
      } else if (this.value === undefined || this.value === null) {
        this.parsingValue = true;
        this.value = [];
        this.parsingValue = false;
      }
    } else if (Array.isArray(this.value)) {
      this.parsingValue = true;
      this.value = this.value[0];
      this.parsingValue = false;
    }
  }

  private optionLabel(option: FxSelectOptionEl): string {
    if (option.label !== undefined && option.label !== null) {
      return option.label;
    }
    const text = (option.textContent || "").trim();
    return text.length > 0 ? text : option.value;
  }

  private syncOptions(): void {
    const selected = this.getValueArray();

    this.options.forEach((option) => {
      option.selected = selected.includes(option.value);
      option.active = this.open && option.value === this.activeValue;
    });

    this.optionsVersion++;
  }

  private applySearch(): void {
    const token = ++this.searchToken;
    const query = this.query.trim();

    const finish = (
      results: FxListSearchResult<{ value: string; label: string; disabled: boolean }>[]
    ) => {
      if (token !== this.searchToken) {
        return;
      }

      const visibleValues = new Set(results.map((result) => result.item.value));
      const labelMatches = new Map<string, FxListMatch[]>();
      results.forEach((result) => {
        if (result.matches) {
          labelMatches.set(result.item.value, result.matches);
        }
      });

      let visible = 0;
      this.options.forEach((option) => {
        const isVisible = visibleValues.has(option.value);
        option.filtered = !isVisible;
        option.highlightQuery =
          this.search && query.length > 0 && labelMatches.has(option.value)
            ? query
            : undefined;
        option.selected = this.getValueArray().includes(option.value);
        option.active = this.open && option.value === this.activeValue;
        if (isVisible) {
          visible++;
        }
      });

      this.visibleCount = visible;
      this.optionsVersion++;
    };

    const items = this.options.map((option) => ({
      value: option.value,
      label: this.optionLabel(option),
      disabled: option.disabled,
    }));

    if (!this.search || query.length === 0) {
      finish(items.map((item) => ({ item })));
      return;
    }

    if (this.customSearch) {
      const list: FxSelectOption[] = items.map((item) => ({
        ...item,
        selected: this.getValueArray().includes(item.value),
      }));

      Promise.resolve(this.customSearch(query, list))
        .then((result) => {
          const visible = new Set(result.map((option) => option.value));
          finish(items.filter((item) => visible.has(item.value)).map((item) => ({ item })));
        })
        .catch(() => {
          finish([]);
        });
      return;
    }

    finish(defaultListSearch(query, items));
  }

  private getNavigableOptions(): FxSelectOptionEl[] {
    return this.options.filter(
      (option) => !option.disabled && !option.filtered
    );
  }

  private selectValue(optionValue: string): void {
    if (this.disabled || this.readonly) {
      return;
    }

    if (this.multiple) {
      const current = this.getValueArray();
      const next = current.includes(optionValue)
        ? current.filter((entry) => entry !== optionValue)
        : [...current, optionValue];
      this.commitValue(next);
      this.activeValue = optionValue;
      // Keep the list open for multiple selection.
    } else {
      // Re-selecting the current value is a semantic no-op: just close.
      if (this.getValueArray()[0] === optionValue) {
        this.activeValue = optionValue;
        this.close(true);
        return;
      }
      this.commitValue(optionValue);
      this.activeValue = optionValue;
      this.close(true);
    }
  }

  private commitValue(next: string | string[]): void {
    // Guard the `value` watcher so the sync/form work below runs exactly once.
    this.committingValue = true;
    this.value = next;
    this.parseValueAttribute();
    this.syncFormValue();
    this.syncOptions();
    this.committingValue = false;
    this.fxChange.emit({ value: this.value as string | string[] });
  }

  private syncFormValue(): void {
    if (!this.internals || typeof this.internals.setFormValue !== "function") {
      return;
    }

    // Disabled controls are excluded from form submission (native behaviour).
    if (this.disabled) {
      this.internals.setFormValue(null);
    } else if (this.multiple) {
      const values = this.getValueArray();
      if (this.name) {
        const data = new FormData();
        values.forEach((entry) => data.append(this.name as string, entry));
        this.internals.setFormValue(data);
      } else {
        // JSON so values containing commas survive a round-trip (a plain
        // comma-join would be ambiguous for such values).
        this.internals.setFormValue(values.length ? JSON.stringify(values) : null);
      }
    } else {
      const single = this.getValueArray()[0];
      this.internals.setFormValue(single ?? null);
    }

    this.syncValidity();
  }

  /**
   * Reflects the control's validity to the form. A `required` select with no
   * selection is invalid (`valueMissing`) so it blocks form submission. A
   * disabled control never participates in constraint validation.
   */
  private syncValidity(): void {
    if (!this.internals || typeof this.internals.setValidity !== "function") {
      return;
    }

    const isEmpty = this.getValueArray().length === 0;
    if (!this.disabled && this.required && isEmpty) {
      this.internals.setValidity(
        { valueMissing: true },
        "Please select an option.",
        this.triggerRef
      );
    } else {
      this.internals.setValidity({});
    }
  }

  private open_(): void {
    if (this.disabled || this.open) {
      return;
    }
    this.open = true;
    const selected = this.getValueArray();
    const navigable = this.getNavigableOptions();
    this.activeValue =
      navigable.find((option) => selected.includes(option.value))?.value ??
      navigable[0]?.value;
    this.syncOptions();
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
    window.addEventListener("resize", this.onReposition);
    window.addEventListener("scroll", this.onReposition, true);

    this.openFrame = requestAnimationFrame(() => {
      this.openFrame = undefined;
      this.updatePlacement();
      if (this.search) {
        this.searchInputRef?.focus();
      }
      this.scrollActiveIntoView();
    });
  }

  /**
   * Measures the space around the trigger and flips the list above it when the
   * dropdown would be clipped by the bottom viewport edge and there is more
   * room above.
   */
  private updatePlacement(): void {
    if (!this.open || !this.triggerRef || !this.listRef) {
      return;
    }

    this.placement = computeDropdownPlacement({
      triggerRect: this.triggerRef.getBoundingClientRect(),
      popoverHeight: this.listRef.offsetHeight,
      viewportHeight: window.innerHeight || document.documentElement.clientHeight,
    });
  }

  private close(restoreFocus = false): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.activeValue = undefined;
    this.placement = "bottom";
    // Reset the search so a stale query/filter does not persist on reopen.
    if (this.query !== "") {
      this.query = "";
      this.applySearch();
    }
    this.syncOptions();
    if (this.repositionFrame !== undefined) {
      cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = undefined;
    }
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    window.removeEventListener("resize", this.onReposition);
    window.removeEventListener("scroll", this.onReposition, true);

    // Return focus to the trigger for keyboard-driven closes so focus is not
    // orphaned on the now-hidden search input (ARIA combobox pattern).
    if (restoreFocus && !this.disabled) {
      this.triggerRef?.focus();
    }
  }

  private toggleOpen(): void {
    if (this.open) {
      this.close();
    } else {
      this.open_();
    }
  }

  private moveActive(delta: number): void {
    const navigable = this.getNavigableOptions();
    if (navigable.length === 0) {
      return;
    }

    const currentIndex = navigable.findIndex(
      (option) => option.value === this.activeValue
    );
    let nextIndex = currentIndex + delta;
    if (nextIndex < 0) {
      nextIndex = navigable.length - 1;
    } else if (nextIndex >= navigable.length) {
      nextIndex = 0;
    }

    this.activeValue = navigable[nextIndex].value;
    this.syncOptions();
    this.scrollActiveIntoView();
  }

  /** Keeps the keyboard-active option visible within the scrollable list. */
  private scrollActiveIntoView(): void {
    if (this.activeValue === undefined) {
      return;
    }
    const active = this.options.find(
      (option) => option.value === this.activeValue
    );
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ block: "nearest" });
    }
  }

  // --------------------------------------------
  // Handlers
  // --------------------------------------------

  private handleTriggerClick = () => {
    if (this.disabled) {
      return;
    }
    this.toggleOpen();
  };

  /**
   * Syncs the keyboard-active option with the hovered one so pointer and
   * keyboard highlights never diverge (and Enter selects the hovered option).
   */
  @Listen("optionHovered")
  handleOptionHovered(ev: CustomEvent<{ value: string }>) {
    if (!this.open) {
      return;
    }
    const value = ev.detail?.value;
    const navigable = this.getNavigableOptions();
    if (value === undefined || !navigable.some((o) => o.value === value)) {
      return;
    }
    if (this.activeValue === value) {
      return;
    }
    this.activeValue = value;
    this.syncOptions();
  }

  private handleFocus = (ev: FocusEvent) => {
    // Suppress when focus moves between internal shadow parts (e.g. trigger to
    // the search input) — the control did not actually gain focus.
    if (this.isInternalFocusTarget(ev.relatedTarget)) {
      return;
    }
    this.fxFocus.emit();
  };

  private handleBlur = (ev: FocusEvent) => {
    // Suppress when focus stays inside the component (e.g. trigger to search).
    if (this.isInternalFocusTarget(ev.relatedTarget)) {
      return;
    }
    // Focus moved to a real element outside the component (e.g. Tab): close the
    // list. A null relatedTarget (e.g. clicking a non-focusable option) is left
    // to the pointerdown/selection handlers so multiple-select stays open.
    if (this.open && ev.relatedTarget) {
      this.close();
    }
    this.fxBlur.emit();
  };

  private handleKeyDown = (ev: KeyboardEvent) => {
    if (this.disabled) {
      return;
    }

    switch (ev.key) {
      case "ArrowDown":
        ev.preventDefault();
        if (!this.open) {
          this.open_();
        } else {
          this.moveActive(1);
        }
        break;
      case "ArrowUp":
        ev.preventDefault();
        if (!this.open) {
          this.open_();
        } else {
          this.moveActive(-1);
        }
        break;
      case "Enter":
        if (this.open && this.activeValue !== undefined) {
          ev.preventDefault();
          this.selectValue(this.activeValue);
        } else if (!this.open) {
          ev.preventDefault();
          this.open_();
        }
        break;
      case " ":
        if (!this.open) {
          ev.preventDefault();
          this.open_();
        }
        break;
      case "Escape":
        if (this.open) {
          ev.preventDefault();
          this.close(true);
        }
        break;
      default:
        break;
    }
  };

  private handleSearchInput = (ev: Event) => {
    const input = ev.target as HTMLInputElement;
    this.query = input.value;
    this.activeValue = undefined;
    this.fxInput.emit({ query: this.query });
    this.applySearch();
  };

  private handleSearchKeyDown = (ev: KeyboardEvent) => {
    // Enter must never submit the enclosing form from the search input, even
    // when there is no active option to select yet.
    if (ev.key === "Enter") {
      ev.preventDefault();
    }
    // Handle navigation/selection keys here and stop propagation so the host's
    // onKeyDown does not run the same handler a second time.
    if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(ev.key)) {
      ev.stopPropagation();
      this.handleKeyDown(ev);
    }
  };

  // --------------------------------------------
  // Render
  // --------------------------------------------

  /**
   * The consumer-provided accessible name, forwarded from the host so the
   * focusable trigger/search control has a meaningful name even when only a
   * placeholder is shown.
   */
  private get hostAriaLabel(): string | null {
    return this.el.getAttribute("aria-label");
  }

  /**
   * The accessible name for the trigger button. Prefers a consumer-provided
   * host `aria-label`. Otherwise the visible content names the button — the
   * selected value(s) or the `placeholder` slot text — so no `aria-label` is
   * forwarded in those cases. Only when nothing would otherwise name the
   * button (no host label, no selection, no placeholder) is a generic default
   * used so the control is never nameless.
   */
  private get triggerAriaLabel(): string | undefined {
    const host = this.hostAriaLabel;
    if (host) {
      return host;
    }
    if (this.getValueArray().length > 0) {
      return undefined;
    }
    const hasPlaceholder = !!this.el.querySelector('[slot="placeholder"]');
    return hasPlaceholder ? undefined : "Select";
  }

  private renderTriggerLabel() {
    const selected = this.getValueArray();
    if (selected.length === 0) {
      return (
        <span part="placeholder">
          <slot name="placeholder" />
        </span>
      );
    }

    const labels = selected.map((value) => {
      const option = this.options.find((entry) => entry.value === value);
      return option ? this.optionLabel(option) : value;
    });

    return <span part="value">{labels.join(", ")}</span>;
  }

  render() {
    const {
      disabled,
      readonly,
      multiple,
      loading,
      search,
      size,
      variant,
      color,
      open,
      query,
      required,
      visibleCount,
      placement,
      optionsReady,
    } = this;

    // Referenced so the render re-runs when the option set changes.
    void this.optionsVersion;

    return (
      <Host
        data-size={size}
        data-variant={variant}
        data-color={color}
        data-multiple={multiple ? "" : undefined}
        data-disabled={disabled ? "" : undefined}
        data-readonly={readonly ? "" : undefined}
        data-loading={loading ? "" : undefined}
        data-search={search ? "" : undefined}
        data-open={open ? "" : undefined}
        data-placement={open ? placement : undefined}
        data-required={required ? "" : undefined}
        aria-disabled={disabled ? "true" : null}
        aria-readonly={readonly ? "true" : null}
        aria-expanded={open ? "true" : "false"}
        aria-haspopup="listbox"
        name={this.name}
        class={buildColorClasses(color as TThemeColors)}
        onKeyDown={this.handleKeyDown}
      >
        <button
          type="button"
          part="trigger"
          disabled={disabled}
          aria-label={this.triggerAriaLabel}
          aria-haspopup="listbox"
          aria-controls={this.listId}
          aria-expanded={open ? "true" : "false"}
          aria-activedescendant={this.search ? undefined : this.activeDescendantId}
          tabIndex={disabled ? -1 : 0}
          ref={(el) => (this.triggerRef = el as HTMLButtonElement)}
          onClick={this.handleTriggerClick}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
        >
          {this.renderTriggerLabel()}
          <span part="indicator" aria-hidden="true"></span>
        </button>

        <div
          part="popover"
          ref={(el) => (this.listRef = el as HTMLDivElement)}
        >
          <span part="status" role="status" aria-live="polite" aria-atomic="true">
            {this.activeAnnouncement}
          </span>

          {search && (
            <input
              part="search-input"
              type="text"
              value={query}
              placeholder="Search…"
              disabled={disabled}
              aria-label={this.hostAriaLabel}
              ref={(el) => (this.searchInputRef = el as HTMLInputElement)}
              role="combobox"
              aria-expanded={open ? "true" : "false"}
              aria-controls={this.listId}
              aria-autocomplete="list"
              aria-activedescendant={this.activeDescendantId}
              onInput={this.handleSearchInput}
              onKeyDown={this.handleSearchKeyDown}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
            />
          )}

          {loading && (
            <div part="loading" aria-live="polite">
              <slot name="loading">Loading…</slot>
            </div>
          )}

          <div
            part="list"
            id={this.listId}
            role="listbox"
            aria-multiselectable={multiple ? "true" : "false"}
          >
            <slot />
          </div>

          {optionsReady && !loading && visibleCount === 0 && (
            <div part="empty">
              <slot name="empty">No options</slot>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
